(() => {
  const SAMPLE_KEY = "curtainsuk_staging_samples_v1";
  const PROJECT_KEY = "curtainsuk_staging_project_v1";
  const EVALUATION_KEY = "curtainsuk_staging_evaluation_v1";
  const RECEIPT_KEY = "curtainsuk_staging_submission_v1";
  const REVIEW_RESUME_KEY = "curtainsuk_staging_review_resume_v1";
  const NEW_CURTAIN_DRAFT_KEY = "curtainsuk_new_curtain_draft_v1";
  const OPENAI_ATTRIBUTION_KEY = "curtainsuk_openai_ads_attribution_v1";
  const REVIEW_REQUEST_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const REVIEW_ACCEPTANCE_TOKEN = /^v1\.\d{10,12}\.[A-Za-z0-9_-]{43}$/;
  const FABRIC_MASTER_ID = /^[a-zA-Z0-9-]{1,150}$/;

  const emit = (name, detail = {}) => {
    const payload = { event: `curtainsuk_${name}`, ...detail };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    window.dispatchEvent(new CustomEvent("curtainsuk:analytics", { detail: payload }));
  };

  const money = (minor, currency = "GBP") => new Intl.NumberFormat("en-GB", {
    style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(minor / 100);

  const readJson = (key, fallback) => {
    try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
  };

  const openAiAttribution = () => {
    const saved = readJson(OPENAI_ATTRIBUTION_KEY, null);
    if (!saved || typeof saved !== "object" || Array.isArray(saved)) return undefined;
    const safe = {};
    for (const key of ["oppref", "obref"]) {
      const value = saved[key];
      if (typeof value === "string" && value.trim() && value.length <= 1024 && !/[\u0000-\u001f\u007f]/.test(value)) safe[key] = value.trim();
    }
    return Object.keys(safe).length ? safe : undefined;
  };

  const endpoint = (root, path) => `${root.replace(/\/$/, "")}/${path}`;
  const escapeHtml = (value) => String(value ?? "").replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character]);

  function humanise(value) {
    return String(value ?? "").replaceAll("_", " ").toLowerCase().replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
  }

  function reviewedResumeCapability() {
    const fragment = new URLSearchParams(location.hash.startsWith("#") ? location.hash.slice(1) : location.hash);
    const fragmentContainsCapability = fragment.has("cuk_review") || fragment.has("cuk_token");
    if (fragmentContainsCapability) {
      const reviewRequestId = fragment.get("cuk_review") || "";
      const reviewAcceptanceToken = fragment.get("cuk_token") || "";
      const cleanUrl = new URL(location.href);
      cleanUrl.hash = "";
      history.replaceState(history.state, "", cleanUrl);
      if (!REVIEW_REQUEST_ID.test(reviewRequestId) || !REVIEW_ACCEPTANCE_TOKEN.test(reviewAcceptanceToken)) {
        sessionStorage.removeItem(REVIEW_RESUME_KEY);
        return { error: "This review link is incomplete or invalid. Ask the curtain team for a new link." };
      }
      const capability = { reviewRequestId, reviewAcceptanceToken };
      sessionStorage.setItem(REVIEW_RESUME_KEY, JSON.stringify(capability));
      return { capability };
    }
    try {
      const capability = JSON.parse(sessionStorage.getItem(REVIEW_RESUME_KEY) || "null");
      if (capability
          && REVIEW_REQUEST_ID.test(capability.reviewRequestId)
          && REVIEW_ACCEPTANCE_TOKEN.test(capability.reviewAcceptanceToken)) {
        return { capability };
      }
    } catch {
      sessionStorage.removeItem(REVIEW_RESUME_KEY);
    }
    return {};
  }

  function checkoutUrlFor(root, handoff) {
    if (root.dataset.purchaseControlsEnabled !== "true") return null;
    const production = root.dataset.productionCheckout === "true";
    const created = production ? "PRODUCTION_DRAFT_CREATED" : "TEST_DRAFT_CREATED";
    const reused = production ? "EXISTING_PRODUCTION_DRAFT_REUSED" : "EXISTING_TEST_DRAFT_REUSED";
    if (!handoff || handoff.paymentEnabled !== production
        || ![created, reused].includes(handoff.testCheckoutStatus)
        || handoff.shopifyWritePerformed !== (handoff.testCheckoutStatus === created)
        || typeof handoff.checkoutUrl !== "string") return null;
    let checkoutUrl;
    try { checkoutUrl = new URL(handoff.checkoutUrl); } catch { return null; }
    const allowedHosts = production ? ["www.curtainsuk.com", "carpetup.myshopify.com"] : [String(root.dataset.stagingCheckoutHost || "").trim().toLowerCase()];
    if (checkoutUrl.protocol !== "https:" || !allowedHosts.includes(checkoutUrl.hostname.toLowerCase()) || checkoutUrl.username || checkoutUrl.password || checkoutUrl.port) return null;
    return checkoutUrl.toString();
  }

  function renderHandoffConfirmation(root, confirmation, handoff) {
    const checkoutUrl = checkoutUrlFor(root, handoff);
    confirmation.replaceChildren();
    const message = document.createElement("p");
    message.textContent = handoff.message || "Checkout handoff prepared.";
    const reference = document.createElement("p");
    reference.className = "cuk-hint";
    reference.textContent = handoff.paymentEnabled ? `Reference ${handoff.handoffId}` : `Reference ${handoff.handoffId} · Real payment and manufacture remain disabled.`;
    const amounts = document.createElement("p");
    amounts.textContent = `Curtains ${money(handoff.goodsPriceGrossAmountMinor)} · Delivery ${money(handoff.shippingGrossAmountMinor)} · Total ${money(handoff.goodsPriceGrossAmountMinor + handoff.shippingGrossAmountMinor)}`;
    confirmation.append(message, amounts, reference);
    if (["TEST_DRAFT_CREATED", "EXISTING_TEST_DRAFT_REUSED", "PRODUCTION_DRAFT_CREATED", "EXISTING_PRODUCTION_DRAFT_REUSED"].includes(handoff.testCheckoutStatus) && !checkoutUrl) {
      throw new Error("Your order reference was prepared, but its secure checkout address could not be verified. Please contact enquiries@curtainsuk.com quoting the reference. Do not create another configuration to retry.");
    }
    if (checkoutUrl) {
      const link = document.createElement("a");
      link.className = "cuk-button";
      link.href = checkoutUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = handoff.paymentEnabled ? "Continue to secure checkout" : "Continue to Shopify test checkout";
      link.addEventListener("click", () => emit(handoff.paymentEnabled ? "checkout_opened" : "test_checkout_opened", {
        handoff_id: handoff.handoffId,
        amount_minor: handoff.goodsPriceGrossAmountMinor + handoff.shippingGrossAmountMinor,
        currency: handoff.currency || "GBP",
        order_kind: "curtain",
        content_id: handoff.snapshotId,
        content_type: "curtain_order",
      }));
      confirmation.append(link);
    }
    confirmation.classList.remove("cuk-hidden");
    confirmation.focus();
  }

  function measurementSummary(measurements) {
    if (!measurements || typeof measurements !== "object" || Array.isArray(measurements)) return "Measurements held in the approved revision";
    return Object.entries(measurements)
      .filter(([, value]) => ["string", "number", "boolean"].includes(typeof value))
      .map(([key, value]) => `${humanise(key)}: ${value}${typeof value === "number" && key !== "number_of_sections" ? " cm" : ""}`)
      .join(" · ") || "Measurements held in the approved revision";
  }

  async function fetchJson(url, options = {}) {
    const isFormData = options.body instanceof FormData;
    const response = await fetch(url, {
      credentials: url.startsWith(location.origin) ? "same-origin" : "omit",
      mode: "cors",
      headers: { "Accept": "application/json", ...(isFormData ? {} : { "Content-Type": "application/json" }), ...(options.headers || {}) },
      ...options,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.error || "This request could not be completed.");
      error.status = response.status;
      const code = response.headers?.get?.('X-CurtainsUK-Diagnostic-Code');
      error.diagnosticCode = /^[A-Z][A-Z0-9_]{1,63}$/.test(code || '') ? code : null;
      throw error;
    }
    return data;
  }

  // Ordinary catalogue GETs only. Fiona's capability request and all mutations
  // retain their existing request policy.
  async function fetchBrowseJson(url, signal) {
    for (let attempt = 0; attempt < 2; attempt++) {
      if (signal.aborted) throw new DOMException("Browse request replaced", "AbortError");
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener("abort", abort, { once: true });
      const timeout = setTimeout(abort, 15000);
      let retry = false, backoff = 350;
      try {
        const response = await fetch(url, {
          method: "GET", credentials: url.startsWith(location.origin) ? "same-origin" : "omit",
          mode: "cors", headers: { Accept: "application/json" }, signal: controller.signal,
        });
        let data;
        try { data = await response.json(); }
        catch (error) { if (controller.signal.aborted || response.ok) throw error; data = {}; }
        if (response.ok) return data;
        retry = [429, 500, 502, 503, 504].includes(response.status);
        const error = new Error(data.error || "The fabrics could not be refreshed just now.");
        error.status = response.status;
        if (response.status === 429) {
          const value = response.headers.get("Retry-After");
          error.retryAfter = value === null ? 0 : /^\d+(\.\d+)?$/.test(value) ? Number(value) : Infinity;
          if (error.retryAfter <= 2) backoff = Math.max(backoff, error.retryAfter * 1000);
        }
        throw error;
      } catch (error) {
        if (signal.aborted) throw new DOMException("Browse request replaced", "AbortError");
        // Fetch failures and timeouts are retryable; ordinary 4xx and other
        // server responses are not. A long Retry-After must not hold the UI.
        const retryAfter = error.status === 429 ? Number(error.retryAfter) : 0;
        if (error.name === "AbortError" || error instanceof TypeError) retry = true;
        if (attempt === 1 || !retry || (error.status === 429 && retryAfter > 2)) throw error;
      } finally {
        clearTimeout(timeout);
        signal.removeEventListener("abort", abort);
      }
      await new Promise((resolve, reject) => {
        const wait = setTimeout(() => { signal.removeEventListener("abort", cancelled); resolve(); }, backoff);
        const cancelled = () => { clearTimeout(wait); reject(new DOMException("Browse request replaced", "AbortError")); };
        signal.addEventListener("abort", cancelled, { once: true });
        if (signal.aborted) cancelled();
      });
    }
  }

  async function fetchNailaBrowse(root, destination, signal) {
    const url = new URL(destination, location.origin);
    if (root.dataset.nailaEnabled !== 'true' || root.dataset.nailaActive !== 'true' ||
      url.protocol !== 'https:' || url.origin !== location.origin || url.username || url.password || url.hash ||
      url.pathname !== '/apps/curtainsuk-decision/catalog' || url.searchParams.getAll('naila').length !== 1 ||
      url.searchParams.get('naila') !== '1' || url.searchParams.getAll('view').length !== 1 ||
      url.searchParams.get('view') !== 'retail' || url.searchParams.has('fabric') ||
      typeof root.cukNailaBrowseProof !== 'function')
      throw new Error('Your consultation connection is not ready. Please continue with Fiona and retry.');
    const proof = await root.cukNailaBrowseProof(url.href);
    if (signal?.aborted) throw new DOMException("Browse request replaced", "AbortError");
    if (root.dataset.nailaEnabled !== 'true' || root.dataset.nailaActive !== 'true' ||
      typeof proof?.['x-curtainsuk-naila-capability'] !== 'string' || !proof['x-curtainsuk-naila-capability'] ||
      proof['x-curtainsuk-naila-capability'].length > 180 ||
      !/^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(proof?.['x-curtainsuk-naila-session'] ?? ''))
      throw new Error('Your consultation connection is not ready. Please continue with Fiona and retry.');
    return fetchJson(url.href, {
      method: 'GET', credentials: 'same-origin', redirect: 'error', signal,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json',
        'x-curtainsuk-naila-capability': proof['x-curtainsuk-naila-capability'],
        'x-curtainsuk-naila-session': proof['x-curtainsuk-naila-session'] },
    });
  }

  function rememberProject(values) {
    const current = readJson(PROJECT_KEY, {});
    localStorage.setItem(PROJECT_KEY, JSON.stringify({ ...current, ...values, updatedAt: new Date().toISOString() }));
  }

  function addSample(fabric, windowSlug) {
    const samples = readJson(SAMPLE_KEY, []);
    const hci = readJson("curtainsuk_hci_context_v1", null);
    const consultationContext = hci?.fabricMasterId === fabric.id ? hci : null;
    const existing = samples.find((item) => item.fabricId === fabric.id);
    if (existing) {
      if (windowSlug) existing.windowSlug = windowSlug;
      if (consultationContext) existing.consultationContext = consultationContext;
    } else samples.push({ fabricId: fabric.id, supplier: fabric.supplier, brand: fabric.brand, design: fabric.design, colour: fabric.colour, windowSlug, consultationContext, addedAt: new Date().toISOString() });
    localStorage.setItem(SAMPLE_KEY, JSON.stringify(samples));
    rememberProject({ fabricId: fabric.id, windowSlug: windowSlug || "standard-window" });
    emit("sample_intent", { fabric_id: fabric.id, window_type: windowSlug || null });
    renderBaskets();
  }

  function renderBaskets() {
    const samples = readJson(SAMPLE_KEY, []);
    const remembered = readJson(PROJECT_KEY, {});
    document.querySelectorAll("[data-cuk-sample-basket]").forEach((root) => {
      const list = root.querySelector("[data-cuk-sample-list]");
      const empty = root.querySelector("[data-cuk-sample-empty]");
      if (!list || !empty) return;
      list.innerHTML = "";
      empty.hidden = samples.length > 0;
      samples.forEach((sample) => {
        const item = document.createElement("li");
        item.textContent = `${sample.design} · ${sample.colour}`;
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'cuk-button cuk-button--secondary';
        button.textContent = root.dataset.purchaseControlsEnabled === 'true' ? 'Add sample to basket' : 'Check sample availability';
        button.addEventListener('click', () => window.dispatchEvent(new CustomEvent('curtainsuk:sample-add',{detail:{root,sample,button}})));
        item.appendChild(document.createElement('br')); item.appendChild(button);
        list.appendChild(item);
      });
    });
    document.querySelectorAll("[data-cuk-resume]").forEach((link) => {
      const latestSample = samples[samples.length - 1];
      const fabricId = latestSample?.fabricId || remembered.fabricId;
      const windowSlug = latestSample?.windowSlug || remembered.windowSlug || "standard-window";
      link.href = `/pages/curtain-visualiser?window=${encodeURIComponent(windowSlug)}${fabricId ? `&fabric=${encodeURIComponent(fabricId)}` : ""}`;
    });
  }

  function renderFabricCards(root, catalog) {
    const grid = root.querySelector("[data-cuk-fabric-grid]");
    if (!grid) return;
    const visualiser = root.hasAttribute('data-cuk-visualiser');
    const params = new URLSearchParams(location.search);
    const selectedWindow = visualiser ? '' : params.get("window") || readJson(PROJECT_KEY, {}).windowSlug || "standard-window";
    grid.innerHTML = "";
    const count = root.querySelector("[data-cuk-fabric-count]");
    if (count) count.textContent = visualiser
      ? catalog.refineSearch ? 'Narrow your search to find a room-ready fabric'
      : `${catalog.fabrics.length} ${catalog.fabrics.length === 1 ? 'fabric' : 'fabrics'} · Page ${catalog.page || 1}`
      : `${catalog.total ?? catalog.fabrics.length} ${(catalog.total ?? catalog.fabrics.length) === 1 ? 'fabric' : 'fabrics'} · Page ${catalog.page || 1} of ${Math.max(1, catalog.pages || 1)}`;
    if (catalog.fabrics.length === 0) {
      grid.innerHTML = visualiser && catalog.refineSearch
        ? '<p class="cuk-empty">Try a more specific design or colourway to find a fabric with a room preview.</p>'
        : '<p class="cuk-empty">No fabrics match those filters.</p>';
      return;
    }
    catalog.fabrics.forEach((fabric) => {
      const card = document.createElement("article");
      card.className = "cuk-fabric";
      const repeat = Number.isFinite(fabric.verticalRepeatMm)
        ? fabric.verticalRepeatMm > 0 ? `${fabric.verticalRepeatMm / 10} cm vertical repeat` : "No vertical repeat"
        : "Pattern repeat to be confirmed";
      const usableWidth = Number.isFinite(fabric.usableWidthMm) && fabric.usableWidthMm > 0
        ? `${fabric.usableWidthMm / 10} cm usable width`
        : "Usable width to be confirmed";
      const composition = (fabric.composition || []).map((part) => `${part.percentage}% ${part.material}`).join(", ") || "Composition available on request";
      const image = fabric.imageReferences?.[0];
      const visual = image
        ? `<img width="600" height="600" src="${escapeHtml(image)}?width=600" srcset="${escapeHtml(image)}?width=400 400w, ${escapeHtml(image)}?width=800 800w" sizes="(max-width: 749px) 90vw, 25vw" alt="${escapeHtml(fabric.design)} in ${escapeHtml(fabric.colour)} by ${escapeHtml(fabric.brand || fabric.supplier)}" loading="lazy">`
        : `<span class="cuk-fabric__placeholder" aria-hidden="true">${escapeHtml(fabric.design?.slice(0, 1) || "F")}</span>`;
      const sampleAction = visualiser
        ? (fabric.roomPreview?.available ? '<button class="cuk-button" type="button" data-cuk-see-in-room>See in room</button>' : '<p class="cuk-hint">Room preview not available for this fabric yet.</p>')
        : fabric.sampleAvailable === true
        ? `<button class="cuk-button cuk-button--secondary" type="button" data-sample>Order sample</button>`
        : `<button class="cuk-button cuk-button--secondary" type="button" disabled>${fabric.sampleAvailable === false ? "Sample unavailable" : "Sample to be confirmed"}</button>`;
      const legacyDetailUrl = `/pages/fabric-library?view=browse-fabrics&fabric=${encodeURIComponent(fabric.id)}&window=${encodeURIComponent(selectedWindow)}`;
      const profileUrl = /^https:\/\/www\.curtainsuk\.com\/pages\/fabric\/[a-z0-9-]+$/i.test(fabric.fabricProfileUrl || "")
        ? fabric.fabricProfileUrl
        : null;
      const viewFabricUrl = profileUrl || legacyDetailUrl;
      const viewFabricAction = `<a class="cuk-button" data-cuk-view-fabric ${visualiser ? 'target="_blank" rel="noopener"' : ''} href="${escapeHtml(viewFabricUrl)}">View Fabric</a>`;
      card.innerHTML = visualiser ? `
        <div class="cuk-fabric__swatch">${visual}</div>
        <div class="cuk-fabric__body">
          <p class="cuk-eyebrow">${escapeHtml(fabric.brand || fabric.supplier)}</p>
          <h3>${escapeHtml(fabric.design)} · ${escapeHtml(fabric.colour)}</h3>
          <div class="cuk-fabric__actions">${sampleAction}${viewFabricAction}</div>
        </div>` : `
        <div class="cuk-fabric__swatch">${visual}</div>
        <div class="cuk-fabric__body">
          <p class="cuk-eyebrow">${escapeHtml(fabric.brand || fabric.supplier)} · ${escapeHtml(fabric.collection)}</p>
          <h3>${escapeHtml(fabric.design)} · ${escapeHtml(fabric.colour)}</h3>
          <p>${usableWidth} · ${repeat}</p>
          <p>${escapeHtml(composition)}</p>
          <p class="cuk-hint">${escapeHtml(fabric.availability)}</p>
          <div class="cuk-fabric__actions">
            ${sampleAction}
            ${viewFabricAction}
            ${fabric.roomPreview?.available ? `<a class="cuk-button" href="/pages/room-visualiser?fabric=${encodeURIComponent(fabric.id)}">See in room</a>` : ""}
          </div>
        </div>`;
      if (!visualiser && root.hasAttribute("data-cuk-shopping")) {
        const body = card.querySelector('.cuk-fabric__body');
        body.querySelector('.cuk-eyebrow').textContent = fabric.brand || fabric.supplier;
        body.querySelector('h3').textContent = fabric.design;
        const paragraphs = [...body.querySelectorAll(':scope > p:not(.cuk-eyebrow):not(.cuk-hint)')];
        paragraphs[0].textContent = fabric.colour;
        paragraphs[0].className = 'cuk-browse-colourway';
        paragraphs[1].textContent = [...new Set([...(fabric.patterns || []), ...(fabric.characters || [])])].filter(v => v && v !== 'UNKNOWN').slice(0, 2).join(' · ');
        paragraphs[1].className = 'cuk-browse-descriptor';
        const price = document.createElement('p'); price.className = 'cuk-browse-price';
        const guide = fabric.browseGuide;
        const guideValid = guide?.currency === 'GBP' && guide.policy === 'curtainsuk-browse-guide-v1' && Number.isSafeInteger(guide.amountMinor) && guide.amountMinor > 0;
        price.textContent = guideValid ? `Curtains from ${new Intl.NumberFormat('en-GB', {style:'currency', currency:'GBP', minimumFractionDigits: guide.amountMinor % 100 ? 2 : 0}).format(guide.amountMinor / 100)}` : 'Price guide unavailable';
        body.insertBefore(price, paragraphs[1]);
        if (guideValid) {
          const disclosure = document.createElement('p'); disclosure.className = 'cuk-browse-price-note';
          disclosure.textContent = 'Price guide. Final price depends on measurements and options.';
          price.after(disclosure);
        }
        const productLink = card.querySelector('[data-cuk-view-fabric]');
        const imageLink = document.createElement('a'); imageLink.href = productLink.href;
        imageLink.setAttribute('aria-label', `View ${fabric.design} in ${fabric.colour}`);
        const swatch = card.querySelector('.cuk-fabric__swatch');
        imageLink.append(...swatch.childNodes); swatch.append(imageLink);
      }
      if (visualiser) card.querySelector('[data-cuk-see-in-room]')?.addEventListener('click', () => {
        const event = new CustomEvent('curtainsuk:visualiser-select', { detail: { fabric }, bubbles: true, cancelable: true });
        if (!root.dispatchEvent(event)) return;
        const target = new URL(root.dataset.visualiserUrl || '/pages/room-visualiser', location.origin);
        if (target.origin !== location.origin) return;
        target.searchParams.set('fabric', fabric.id);
        location.assign(target.href);
      });
      else if (root.hasAttribute('data-cuk-shopping') && window.CurtainsUKFabricExperience) window.CurtainsUKFabricExperience.enhanceCard(card,fabric,selectedWindow,addSample);
      else card.querySelector("[data-sample]")?.addEventListener("click", () => addSample(fabric, selectedWindow));
      if (!visualiser && root.hasAttribute('data-cuk-shopping')) window.CurtainsUKJaneFabricHandoff?.mountCard(card, fabric.id, { browseReady: fabric.browseReady, launchReady: fabric.launchReady });
      card.querySelector("img")?.addEventListener("error", (event) => {
        const swatch = event.currentTarget.closest(".cuk-fabric__swatch");
        if (swatch) swatch.innerHTML = `<span class="cuk-fabric__placeholder" aria-hidden="true">${escapeHtml(fabric.design?.slice(0, 1) || "F")}</span>`;
      }, { once: true });
      grid.appendChild(card);
    });
  }

  const BROWSE_URL_FACETS = ["colour", "pattern", "texture", "finish", "character"];
  const GROUPED_BROWSE_COLOURS = new Set(["beige/taupe", "white/cream", "yellow/gold"]);
  const BROWSE_GUIDE_BANDS = new Set(["under-30", "30-60", "60-100", "100-150", "150-plus", "under-50", "50-100", "150-250", "250-plus"]);

  function restoreBrowseUrlFilters(filters, params) {
    for (const key of BROWSE_URL_FACETS) {
      const values = [...new Set((params.get(key) || "").split(",")
        .map(value => value.trim().toLowerCase())
        .filter(value => value !== "unknown" && (/^[a-z0-9][a-z0-9 -]{0,79}$/.test(value)
          || (key === "colour" && GROUPED_BROWSE_COLOURS.has(value)))))].slice(0, 12);
      let field = filters.elements[key];
      if (!field) { field = document.createElement("input"); field.type = "hidden"; field.name = key; filters.append(field); }
      field.value = values.join(",");
    }
    const guidePrice = filters.elements.guidePrice;
    if (guidePrice) guidePrice.value = BROWSE_GUIDE_BANDS.has(params.get("guidePrice")) ? params.get("guidePrice") : "";
    if (filters.elements.query) filters.elements.query.value = (params.get("query") || "").trim().slice(0, 100);
  }

  async function initFabricBrowser(root, lifecycle) {
    const visualiser = root.hasAttribute('data-cuk-visualiser');
    const on = (target, type, handler) => target.addEventListener(type, handler, { signal: lifecycle.listeners.signal });
    const filters = root.querySelector("[data-cuk-fabric-filters]");
    const filterOptions = root.querySelector("[data-cuk-filter-options]");
    if (filterOptions && window.matchMedia("(max-width: 749px)").matches) filterOptions.open = false;
    const grid = root.querySelector("[data-cuk-fabric-grid]");
    const detail = root.querySelector("[data-cuk-fabric-detail]");
    const navigation = root.querySelector("[data-cuk-pagination]");
    const params = new URLSearchParams(visualiser ? '' : location.search);
    const windowSlug = visualiser ? '' : params.get("window") || readJson(PROJECT_KEY, {}).windowSlug || "";
    let page = Math.max(1, Number(params.get("page")) || 1), generation = 0, timer;
    let visualiserCursor = 0, nextVisualiserCursor = null;
    const visualiserHistory = [];
    let activeRequest, hasResults = false;
    let selectionCatalog = { facets: {} };
    const syncSelection = () => {
      if (!visualiser && root.hasAttribute('data-cuk-shopping')) window.CurtainsUKFabricExperience?.syncSelection?.(root, selectionCatalog, filters);
    };
    const status = root.querySelector("[data-cuk-browse-status]");
    const errorBox = root.querySelector("[data-cuk-error]");
    const previous = navigation.querySelector("[data-cuk-previous]");
    const next = navigation.querySelector("[data-cuk-next]");
    const cancelPending = () => {
      ++generation;
      clearTimeout(timer);
      activeRequest?.abort();
      activeRequest = null;
    };
    lifecycle.cancel = cancelPending;
    const setLoading = () => {
      if (status) status.textContent = hasResults ? "Refreshing fabrics. Previous results are shown while we check prices and availability." : "Loading fabrics…";
      grid.setAttribute("aria-busy", "true");
      grid.inert = hasResults;
      grid.dataset.cukStale = hasResults ? "true" : "false";
      previous.disabled = true; next.disabled = true;
      errorBox.hidden = true;
    };
    const showBrowseError = (error, isNaila) => {
      errorBox.querySelector("[data-cuk-error-message]").textContent = isNaila
        ? error.message || "Your consultation connection could not be refreshed."
        : "We couldn't refresh fabrics just now. Please try again.";
      errorBox.hidden = false;
      if (status) status.textContent = hasResults
        ? "Previous results are shown. Prices and availability have not been refreshed."
        : "Fabrics are unavailable just now.";
      if (!hasResults) grid.replaceChildren();
      grid.inert = hasResults;
      grid.dataset.cukStale = hasResults ? "true" : "false";
      previous.disabled = true; next.disabled = true;
    };
    // Additive presentation bridge. Completely absent when Fiona is disabled.
    let nailaPaused = false;
    if (!visualiser && root.dataset.nailaEnabled === 'true') {
      on(root, 'cuk:naila:pause', () => { nailaPaused = true; cancelPending(); grid.removeAttribute('aria-busy'); });
      on(root, 'cuk:naila:browse', () => { nailaPaused = false; });
      on(root, 'cuk:naila:render', event => {
        if (root.dataset.nailaActive !== 'true') return;
        cancelPending(); grid.removeAttribute('aria-busy'); grid.inert = false; grid.dataset.cukStale = "false";
        renderFabricCards(root, event.detail);
        hasResults = true; if (status) status.textContent = ""; errorBox.hidden = true;
      });
      on(filters, 'input', () => { nailaPaused = false; });
      on(filters, 'reset', () => { nailaPaused = false; });
    }
    const showError = (error) => { const box = root.querySelector("[data-cuk-error]"); box.textContent = error.message || "Unable to load fabrics. Please try again."; box.hidden = false; };
    const apiUrl = () => new URL(endpoint(root.dataset.engineBase, "catalog"), location.origin);
    try {
      if (!visualiser && params.get("fabric")) {
        const premiumDetail = root.hasAttribute('data-cuk-shopping') && window.CurtainsUKFabricExperience;
        if (!premiumDetail) root.removeAttribute('data-cuk-shopping');
        root.querySelector('[data-cuk-browse-assistance]')?.remove();
        const url = premiumDetail && root.dataset.browsePreviewEndpoint ? new URL(root.dataset.browsePreviewEndpoint) : apiUrl();
        url.searchParams.set("view", "retail"); url.searchParams.set("fabric", params.get("fabric"));
        if (premiumDetail) url.searchParams.set('browseGuide','1');
        const { fabric } = await fetchJson(url.href);
        if (!fabric) throw new Error("This fabric is not available to view.");
        filters.hidden = true; grid.hidden = true; navigation.hidden = true; root.querySelector("[data-cuk-fabric-count]").hidden = true;
        detail.hidden = false;
        if (premiumDetail) { window.CurtainsUKFabricExperience.renderDetail(root,fabric,windowSlug,addSample); return; }
        const main = fabric.images?.[0];
        const configureUrl = `/pages/curtain-visualiser?fabric=${encodeURIComponent(fabric.id)}${windowSlug ? `&window=${encodeURIComponent(windowSlug)}` : ""}`;
        const unavailableCurtainButton = '<button type="button" class="cuk-button" disabled>Make Curtains in This Fabric</button>';
        const spec = (label, value) => value ? `<div><dt>${label}</dt><dd>${escapeHtml(value)}</dd></div>` : "";
        detail.innerHTML = `<a href="/pages/fabric-library?view=browse-fabrics${windowSlug ? `&window=${encodeURIComponent(windowSlug)}` : ""}" class="cuk-text-link">← All fabrics</a>
          <div class="cuk-fabric-detail"><div class="cuk-fabric-detail__gallery">${main ? `<img class="cuk-fabric-detail__main" src="${escapeHtml(main.url)}" width="${main.width}" height="${main.height}" alt="${escapeHtml(fabric.metadata.alt)}" fetchpriority="high">` : '<p class="cuk-empty">Fabric photography is being prepared.</p>'}
          <div class="cuk-fabric-detail__additional">${(fabric.images || []).slice(1).map((image) => `<img src="${escapeHtml(image.url)}" width="${image.width}" height="${image.height}" alt="${escapeHtml(fabric.metadata.alt)} · ${escapeHtml(image.imageType.toLowerCase())}" loading="lazy">`).join("")}</div></div>
          <div><p class="cuk-eyebrow">${escapeHtml(fabric.brand)} · ${escapeHtml(fabric.collection)}</p><h2>${escapeHtml(fabric.design)}</h2><p class="cuk-fabric-detail__colour">${escapeHtml(fabric.colour)}</p>
          ${fabric.description ? `<p>${escapeHtml(fabric.description)}</p>` : ""}<p class="cuk-hint">${escapeHtml(fabric.availability)}</p>
          <div class="cuk-fabric-detail__actions"><button class="cuk-button cuk-button--secondary" data-sample ${fabric.sampleAvailable === true ? "" : "disabled"}>${fabric.sampleAvailable === true ? "Order a Sample" : "Sample availability to be confirmed"}</button>
          ${fabric.browseReady ? (fabric.orderReady === true ? `<a class="cuk-button" href="${configureUrl}">Make Curtains in This Fabric</a>` : '<button type="button" class="cuk-button" disabled>Make Curtains in This Fabric</button>') : unavailableCurtainButton}</div>
          <p class="cuk-hint">Price and availability are checked for your measurements before purchase.</p>
          <dl class="cuk-fabric-spec">${spec("Composition", (fabric.composition || []).map((part) => `${part.percentage}% ${part.material}`).join(", "))}${spec("Usable width", fabric.usableWidthMm ? `${fabric.usableWidthMm / 10} cm` : null)}${spec("Full width", fabric.fullWidthMm ? `${fabric.fullWidthMm / 10} cm` : null)}${spec("Vertical repeat", fabric.verticalRepeatMm === null ? null : `${fabric.verticalRepeatMm / 10} cm`)}${spec("Horizontal repeat", fabric.horizontalRepeatMm === null ? null : `${fabric.horizontalRepeatMm / 10} cm`)}${spec("Pattern match", fabric.patternMatchType ? humanise(fabric.patternMatchType) : null)}${spec("Care", (fabric.careInstructions || []).join(", "))}${spec("Headings", (fabric.headings || []).map(humanise).join(", "))}${spec("Applications", (fabric.windowTypes || []).map(humanise).join(", "))}</dl>
          <p class="cuk-hint">Screens vary. A sample helps you check the colour and texture in your own light.</p></div></div>`;
        window.CurtainsUKJaneFabricHandoff?.mount(detail, fabric.id, { browseReady: fabric.browseReady, launchReady: fabric.launchReady, orderReady: fabric.orderReady });
        detail.querySelector("[data-sample]")?.addEventListener("click", () => addSample(fabric, windowSlug));
        document.title = fabric.metadata.title;
        root.querySelector("h1").textContent = fabric.metadata.h1;
        const description = document.querySelector('meta[name="description"]'); if (description) description.content = fabric.metadata.metaDescription;
        // Prepared /fabrics/... canonical paths are not advertised as working routes yet.
        const canonical = document.querySelector('link[rel="canonical"]'); if (canonical) canonical.href = `${location.origin}/pages/fabric-library?fabric=${encodeURIComponent(fabric.id)}`;
        return;
      }
      const load = async () => {
        if (nailaPaused || lifecycle.disposed) return;
        cancelPending();
        const current = generation;
        const controller = new AbortController();
        activeRequest = controller;
        const url = !visualiser && root.hasAttribute('data-cuk-shopping') && root.dataset.browsePreviewEndpoint ? new URL(root.dataset.browsePreviewEndpoint) : apiUrl();
        url.searchParams.set("view", "retail");
        if (visualiser) {
          url.searchParams.set('visualiser', '1');
          url.searchParams.set('cursor', String(visualiserCursor));
        } else url.searchParams.set("page", String(page));
        if (!visualiser && root.hasAttribute('data-cuk-shopping')) url.searchParams.set('browseGuide','1');
        for (const [key, value] of new FormData(filters)) if (String(value).trim()) url.searchParams.set(key, String(value).trim());
        if (!visualiser && root.dataset.nailaEnabled === 'true' && root.dataset.nailaActive === 'true') {
          url.searchParams.set('naila', '1');
          if (root.dataset.nailaPriceLevel) url.searchParams.set('nailaPriceLevel', root.dataset.nailaPriceLevel);
        }
        setLoading();
        try {
          const isNaila = !visualiser && root.dataset.nailaEnabled === 'true' && root.dataset.nailaActive === 'true';
          if (!visualiser && root.hasAttribute('data-cuk-shopping') && !isNaila) url.searchParams.set('browseCards', '1');
          const catalog = isNaila
            ? await fetchNailaBrowse(root, url.href, controller.signal) : await fetchBrowseJson(url.href, controller.signal);
          if (current !== generation || controller.signal.aborted) return;
          // Persist the successful selection for reloads. Live controls use the
          // current form, so pending responses never restore a removed choice.
          if (!visualiser) {
            const address = new URL(location.href); for (const key of [...address.searchParams.keys()]) if (!["window", "preview_theme_id", "view", "makingStudio", "makingStep", "makingReturn"].includes(key)) address.searchParams.delete(key);
            for (const [key, value] of new FormData(filters)) if (String(value).trim()) address.searchParams.set(key, String(value).trim());
            address.searchParams.set("page", String(page)); history.replaceState(null, "", address);
          }
          const guideSelect = filters.elements.guidePrice;
          if (guideSelect?.tagName === "SELECT" && catalog.facets.guidePrices) {
            const selected = guideSelect.value;
            guideSelect.replaceChildren(option('All guide prices', ''), ...catalog.facets.guidePrices.map(band => option(band.label, band.value)));
            guideSelect.value = selected;
          }
          for (const [key, values] of Object.entries({ brand: catalog.facets.brands, collection: catalog.facets.collections, colour: catalog.facets.colour, pattern: catalog.facets.pattern, style: catalog.facets.style, character: catalog.facets.character })) {
            // Browse Fabric Intelligence renders Colour as a governed visual
            // control backed by a hidden input. Only legacy native selects own
            // an options collection, so never assume every named form control
            // can be expanded as a select.
            const select = filters.elements[key];
            if (select?.tagName === "SELECT" && !select.dataset.facetsLoaded) {
              const existing = new Set(Array.from(select.options, (item) => item.value));
              for (const value of Array.isArray(values) ? values : []) {
                if (value !== "UNKNOWN" && !existing.has(value)) {
                  select.appendChild(option(value, value));
                  existing.add(value);
                }
              }
              select.dataset.facetsLoaded = "true";
            }
          }
          if (!visualiser && root.hasAttribute('data-cuk-shopping')) {
            selectionCatalog = catalog;
            window.CurtainsUKFabricExperience?.renderDiscovery(root,catalog,filters);
            if (window.CurtainsUKFabricExperience?.renderActiveFilters) window.CurtainsUKFabricExperience.renderActiveFilters(root,catalog,filters);
            else root.querySelector('[data-cuk-active-filters]').textContent = [...new FormData(filters)].filter(([,value])=>String(value).trim()).map(([key,value])=>key === 'guidePrice' ? `Curtains from: ${catalog.facets.guidePrices.find(band=>band.value===value)?.label || ''}` : humanise(String(value))).join(' · ') || 'All fabrics';
          }
          if (visualiser) { catalog.page = page; nextVisualiserCursor = catalog.nextCursor; }
          renderFabricCards(root, catalog);
          hasResults = true;
          grid.inert = false; grid.dataset.cukStale = "false";
          if (status) status.textContent = "";
          previous.disabled = visualiser ? visualiserHistory.length === 0 : page <= 1;
          next.disabled = visualiser ? nextVisualiserCursor === null : page >= catalog.pages;
          errorBox.hidden = true;
          if (!visualiser && root.dataset.nailaEnabled === 'true') root.dispatchEvent(new CustomEvent('cuk:naila:loaded'));
          if (visualiser) root.dispatchEvent(new CustomEvent('curtainsuk:visualiser-catalog-loaded', { detail: { catalog }, bubbles: true }));
        } catch (error) {
          if (current === generation && !controller.signal.aborted) showBrowseError(error, !visualiser && root.dataset.nailaActive === 'true');
        } finally {
          if (current === generation) { grid.removeAttribute("aria-busy"); activeRequest = null; }
        }
      };
      lifecycle.reload = load;
      if (!visualiser && root.hasAttribute('data-cuk-shopping')) restoreBrowseUrlFilters(filters, params);
      else for (const [key, value] of params) if (filters.elements[key]) {
        const element = filters.elements[key]; if (element.tagName === "SELECT" && value && ![...element.options].some((o) => o.value === value)) element.appendChild(option(value, value)); element.value = value;
      }
      on(filters, 'reset', () => { cancelPending(); setLoading(); timer = setTimeout(() => {
        // Hidden visual-choice inputs reflect value into defaultValue; native reset alone retains them.
        if (visualiser || root.hasAttribute('data-cuk-shopping')) for (const field of filters.elements) if (field.type === 'hidden') field.value = '';
        syncSelection();
        page = 1; if (visualiser) { visualiserCursor = 0; visualiserHistory.length = 0; } load();
      }, 0); });
      on(filters, "submit", (event) => event.preventDefault());
      on(filters, "input", (event) => { cancelPending(); setLoading(); syncSelection(); timer = setTimeout(() => {
        page = 1; if (visualiser) { visualiserCursor = 0; visualiserHistory.length = 0; } load();
      }, !visualiser && root.hasAttribute('data-cuk-shopping') && event.target?.type !== 'search' ? 100 : 300); });
      const retry = errorBox.querySelector("[data-cuk-retry]");
      if (retry) on(retry, "click", () => { clearTimeout(timer); load(); });
      on(previous, "click", async () => {
        if (visualiser) { if (!visualiserHistory.length) return; visualiserCursor = visualiserHistory.pop(); }
        page = Math.max(1, page - 1); await load(); if (!visualiser) grid.scrollIntoView({ block: "start" });
      });
      on(next, "click", async () => {
        if (visualiser) { if (nextVisualiserCursor === null) return; visualiserHistory.push(visualiserCursor); visualiserCursor = nextVisualiserCursor; }
        page++; await load(); if (!visualiser) grid.scrollIntoView({ block: "start" });
      });
      await load();
    } catch (error) { showError(error); }
  }

  function baySectionWidths(root) {
    return [...root.querySelectorAll("[data-cuk-bay-section-width]")].map((field) => Number(field.value));
  }

  function renderBaySections(root, requestedCount, values = []) {
    const container = root.querySelector("[data-cuk-bay-sections]");
    if (!container) return;
    const count = Math.min(8, Math.max(2, Number.parseInt(requestedCount, 10) || 3));
    const defaults = [80, 180, 80];
    container.innerHTML = "";
    for (let index = 0; index < count; index += 1) {
      const field = document.createElement("label");
      field.className = "cuk-bay-section";
      field.innerHTML = `<span>Section ${index + 1}</span><input type="number" min="10" max="600" step="0.1" value="${escapeHtml(values[index] ?? defaults[index] ?? 100)}" data-cuk-bay-section-width required>`;
      container.appendChild(field);
    }
  }

  function projectSnapshot(form, root) {
    const scalarNames = [
      "windowSlug", "measurementBasis", "widthCm", "dropCm", "trackOrPoleFitted", "baySectionCount",
      "cornerSectionOneCm", "cornerSectionTwoCm", "cornerAngleDegrees",
      "baseWidth", "peakHeight", "leftVertical", "rightVertical", "leftSlope", "rightSlope",
      "roughWidthCm", "roughDropCm", "fixingPosition", "awkwardFixingPosition",
      "heading", "fabricId", "lining", "construction", "stackDirection",
    ];
    const snapshot = {};
    scalarNames.forEach((name) => {
      const field = form.elements[name];
      if (field && field.value !== "") snapshot[name] = field.value;
    });
    snapshot.baySectionWidthsCm = baySectionWidths(root);
    return snapshot;
  }

  function restoreProject(form, remembered) {
    Object.entries(remembered).forEach(([name, value]) => {
      const field = form.elements[name];
      if (field && typeof value !== "object" && name !== "updatedAt" && name !== "lastOutcome") field.value = String(value);
    });
  }

  function syncConfiguratorUrl(form) {
    const url = new URL(location.href);
    if (form.elements.windowSlug.value) url.searchParams.set("window", form.elements.windowSlug.value);
    else url.searchParams.delete("window");
    if (form.elements.fabricId.value) url.searchParams.set("fabric", form.elements.fabricId.value);
    history.replaceState(history.state, "", url);
    const browseLink = form.querySelector("[data-cuk-browse-fabrics]");
    if (browseLink) browseLink.href = `/pages/fabric-library?window=${encodeURIComponent(form.elements.windowSlug.value)}`;
  }

  function replaceOptions(select, values, labels) {
    if (!select || !Array.isArray(values) || values.length === 0) return;
    const previous = select.value;
    select.innerHTML = "";
    values.forEach((value) => select.appendChild(option(labels[value] || value.replaceAll("_", " "), value)));
    select.value = values.includes(previous) ? previous : values[0];
  }

  // A retained House curtain is immutable commercial evidence. It must never
  // seed the next window. Pricing creates the next authoritative configuration
  // ID only after this clean draft has been completed and validated.
  function beginNewCurtainDraft(root, form, fabricId) {
    localStorage.removeItem(PROJECT_KEY);
    localStorage.removeItem(EVALUATION_KEY);
    localStorage.removeItem(RECEIPT_KEY);
    sessionStorage.removeItem("cuk_config_step_v1");
    form.reset();
    const fieldNames = [
      "windowSlug", "measurementBasis", "widthCm", "dropCm", "trackOrPoleFitted", "baySectionCount",
      "cornerSectionOneCm", "cornerSectionTwoCm", "cornerAngleDegrees", "baseWidth", "peakHeight",
      "leftVertical", "rightVertical", "leftSlope", "rightSlope", "roughWidthCm", "roughDropCm",
      "fixingPosition", "awkwardFixingPosition", "heading", "lining", "construction", "stackDirection",
    ];
    fieldNames.forEach((name) => {
      const field = form.elements[name];
      if (field) field.value = "";
    });
    if (form.elements.fabricId) form.elements.fabricId.value = fabricId || "";
    root.querySelector("[data-cuk-result]")?.setAttribute("hidden", "");
    root.querySelector("[data-cuk-checkout-form]")?.classList.add("cuk-hidden");
    root.querySelector("[data-cuk-review-form]")?.classList.add("cuk-hidden");
    root.querySelector("[data-cuk-review-confirmation]")?.classList.add("cuk-hidden");
    root.querySelector("[data-cuk-error]")?.setAttribute("hidden", "");
    root.querySelectorAll("[data-cuk-result-spec], [data-cuk-result-price], [data-cuk-route-status]").forEach((item) => {
      item.textContent = "";
    });
    const draftId = crypto.randomUUID();
    sessionStorage.setItem(NEW_CURTAIN_DRAFT_KEY, JSON.stringify({ draftId, startedAt: new Date().toISOString(), fabricId: fabricId || null }));
    root.dispatchEvent(new CustomEvent("cuk:configurator-fresh-draft"));
    return draftId;
  }

  const AUTOMATED_MTM_WINDOWS = new Set([
    "standard-window", "patio-sliding-doors", "french-doors", "bifold-doors", "bay-window",
  ]);

  function setJourneyFields(root, windowType) {
    const isBay = windowType?.slug === "bay-window";
    const isCorner = false;
    const isCurved = false;
    const isSpecialist = false;
    const isAwkward = false;
    const isShapedSpecialist = false;
    const isReview = false;
    const needsEvidence = false;
    root.querySelectorAll("[data-cuk-bay]").forEach((item) => item.classList.toggle("cuk-hidden", !isBay));
    root.querySelectorAll("[data-cuk-corner]").forEach((item) => item.classList.toggle("cuk-hidden", !isCorner));
    root.querySelectorAll("[data-cuk-standard]").forEach((item) => item.classList.toggle("cuk-hidden", isSpecialist));
    root.querySelectorAll("[data-cuk-measurement-basis]").forEach((item) => item.classList.toggle("cuk-hidden", isBay));
    root.querySelectorAll("[data-cuk-width]").forEach((item) => item.classList.toggle("cuk-hidden", false));
    root.querySelectorAll("[data-cuk-specialist-shape]").forEach((item) => item.classList.toggle("cuk-hidden", !isShapedSpecialist));
    root.querySelectorAll("[data-cuk-awkward]").forEach((item) => item.classList.toggle("cuk-hidden", !isAwkward));
    root.querySelectorAll("[data-cuk-review-evidence]").forEach((item) => item.classList.toggle("cuk-hidden", !needsEvidence));
    root.querySelectorAll("[data-cuk-specialist-evidence]").forEach((item) => item.classList.toggle("cuk-hidden", !isSpecialist));
    root.querySelectorAll("[data-cuk-standard] input, [data-cuk-standard] select, [data-cuk-standard] textarea").forEach((field) => { field.disabled = isSpecialist; });
    root.querySelectorAll("[data-cuk-measurement-basis] input, [data-cuk-measurement-basis] select, [data-cuk-measurement-basis] textarea").forEach((field) => { field.disabled = isBay; });
    root.querySelectorAll("[data-cuk-width] input, [data-cuk-width] select, [data-cuk-width] textarea").forEach((field) => { field.disabled = false; });
    root.querySelectorAll("[data-cuk-specialist-shape] input, [data-cuk-specialist-shape] select, [data-cuk-specialist-shape] textarea").forEach((field) => { field.disabled = !isShapedSpecialist; });
    root.querySelectorAll("[data-cuk-awkward] input, [data-cuk-awkward] select, [data-cuk-awkward] textarea").forEach((field) => { field.disabled = !isAwkward; });
    root.querySelectorAll("[data-cuk-review-evidence] input, [data-cuk-review-evidence] select, [data-cuk-review-evidence] textarea").forEach((field) => { field.disabled = !needsEvidence; });
    root.querySelectorAll("[data-cuk-specialist-evidence] input, [data-cuk-specialist-evidence] select, [data-cuk-specialist-evidence] textarea").forEach((field) => { field.disabled = !isSpecialist; });
    root.querySelectorAll("[data-cuk-bay] input, [data-cuk-bay] select, [data-cuk-bay] textarea").forEach((field) => { field.disabled = true; });
    root.querySelectorAll("[data-cuk-corner] input, [data-cuk-corner] select, [data-cuk-corner] textarea").forEach((field) => { field.disabled = !isCorner; });
    const widthLabel = root.querySelector("[data-cuk-width-label]");
    if (widthLabel) widthLabel.textContent = isCurved ? "Track arc length (cm)" : "Width (cm)";
    root.querySelector("[data-cuk-width-hint]")?.classList.toggle("cuk-hidden", !isCurved);
    const form = root.querySelector(".cuk-form");
    if (!windowType) {
      globalThis.window?.CurtainsUKMtmConfigurator?.sync(form, null);
      if (form?.elements.heading) form.elements.heading.value = "";
      if (form?.elements.lining) form.elements.lining.value = "";
      const status = root.querySelector("[data-cuk-route-status]");
      status.textContent = "Choose your window";
      status.classList.remove("cuk-status--review");
      return;
    }
    if (isBay && form?.elements.measurementBasis) form.elements.measurementBasis.value = "TRACK_WIDTH";
    globalThis.window?.CurtainsUKMtmConfigurator?.sync(form, windowType);
    replaceOptions(form?.elements.lining, windowType?.linings, {
      UNLINED: "Unlined", STANDARD: "Standard lining", BLACKOUT: "Blackout lining", THERMAL: "Thermal lining", BONDED: "Bonded lining / interlining",
    });
    const status = root.querySelector("[data-cuk-route-status]");
    status.textContent = isSpecialist ? "Technical review" : isReview ? "Price with review" : "Instant price";
    status.classList.toggle("cuk-status--review", isReview || isSpecialist);
    const submit = root.querySelector(".cuk-form > button[type=submit]");
    if (submit) submit.textContent = isSpecialist ? "Prepare my review" : "Check my price or review route";
  }

  function option(label, value) {
    const element = document.createElement("option");
    element.value = value;
    element.textContent = label;
    return element;
  }

  async function initReviewedAcceptance(root, resume) {
    const panel = root.querySelector("[data-cuk-reviewed-resume]");
    const builder = root.querySelector("[data-cuk-configurator-builder]");
    if (!panel || (!resume.capability && !resume.error)) return false;
    panel.classList.remove("cuk-hidden");
    builder?.classList.add("cuk-hidden");
    root.querySelector("[data-cuk-route-status]").textContent = root.dataset.productionCheckout === "true" ? "Your approved curtains" : "Staff-reviewed staging checkout";
    root.querySelector("[data-cuk-reviewed-clear]")?.addEventListener("click", () => {
      sessionStorage.removeItem(REVIEW_RESUME_KEY);
    });
    const loadError = panel.querySelector("[data-cuk-reviewed-load-error]");
    const loading = panel.querySelector("[data-cuk-reviewed-loading]");
    if (resume.error) {
      loading.hidden = true;
      loadError.textContent = resume.error;
      loadError.hidden = false;
      panel.focus();
      return true;
    }

    try {
      const summary = await fetchJson(endpoint(root.dataset.engineBase, "review-acceptance"), {
        method: "POST",
        body: JSON.stringify(resume.capability),
      });
      if (summary.reviewRequestId !== resume.capability.reviewRequestId
          || summary.reviewState !== "READY_FOR_CHECKOUT"
          || summary.paymentEnabled !== false
          || summary.supplierCommercialDataIncluded !== false
          || !summary.price
          || !Number.isInteger(summary.price.grossAmountMinor)
          || summary.price.grossAmountMinor <= 0
          || summary.price.currency !== "GBP") {
        throw new Error("The approved review summary failed its safety checks.");
      }
      loading.hidden = true;
      panel.querySelector("[data-cuk-reviewed-content]").classList.remove("cuk-hidden");
      panel.querySelector("[data-cuk-reviewed-status]").textContent = summary.pricingOutcome === "MANUAL_QUOTE" ? "Approved staff quote" : "Approved after review";
      panel.querySelector("[data-cuk-reviewed-price]").textContent = `${money(summary.price.grossAmountMinor, summary.price.currency)}`;
      panel.querySelector("[data-cuk-reviewed-reference]").textContent = summary.reference;
      panel.querySelector("[data-cuk-reviewed-window]").textContent = summary.window?.label || "Reviewed curtain";
      panel.querySelector("[data-cuk-reviewed-measurements]").textContent = measurementSummary(summary.measurements);
      panel.querySelector("[data-cuk-reviewed-fabric]").textContent = [summary.fabric?.brand, summary.fabric?.design, summary.fabric?.colour, summary.fabric?.supplierSku ? `Ref ${summary.fabric.supplierSku}` : null].filter(Boolean).join(" · ");
      panel.querySelector("[data-cuk-reviewed-heading]").textContent = humanise(summary.heading);
      panel.querySelector("[data-cuk-reviewed-lining]").textContent = humanise(summary.lining);
      panel.querySelector("[data-cuk-reviewed-construction]").textContent = humanise(summary.construction);
      panel.querySelector("[data-cuk-reviewed-availability]").textContent = summary.availability?.label || "Availability to be confirmed";
      panel.querySelector("[data-cuk-reviewed-price-summary]").textContent = `${money(summary.price.grossAmountMinor, summary.price.currency)}`;
      panel.querySelector("[data-cuk-reviewed-delivery]").textContent = summary.delivery?.label || "Delivery shown separately";

      const form = panel.querySelector("[data-cuk-reviewed-checkout-form]");
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        if (root.dataset.purchaseControlsEnabled !== "true") return;
        const error = form.querySelector("[data-cuk-reviewed-error]");
        error.hidden = true;
        if (!form.reportValidity()) return;
        const submit = form.querySelector("button[type=submit]");
        submit.disabled = true;
        submit.textContent = "Re-checking approved revision…";
        emit("quote_accepted", {
          window_type: summary.window.slug,
          outcome: summary.pricingOutcome,
          configuration_id: summary.configurationId,
        });
        try {
          const handoff = await fetchJson(endpoint(root.dataset.engineBase, "checkout-handoff"), {
            method: "POST",
            body: JSON.stringify({
              reviewRequestId: resume.capability.reviewRequestId,
              reviewAcceptanceToken: resume.capability.reviewAcceptanceToken,
              customerAccepted: form.elements.customerAccepted.checked,
              shippingRegion: form.elements.shippingRegion.value,
              shippingPostcode: form.elements.shippingPostcode.value,
            }),
          });
          if (!handoff.prepared) {
            const blockers = Array.isArray(handoff.blockers) ? handoff.blockers.map((item) => humanise(item)).join(", ") : "a required checkout check";
            throw new Error(`${handoff.message || "This reviewed configuration is not ready for checkout"} (${blockers}).`);
          }
          renderHandoffConfirmation(root, form.querySelector("[data-cuk-reviewed-confirmation]"), handoff);
          submit.textContent = "Test checkout prepared";
          emit("checkout_handoff_reached", {
            window_type: summary.window.slug,
            outcome: summary.pricingOutcome,
            configuration_id: summary.configurationId,
            handoff_id: handoff.handoffId,
          });
        } catch (caught) {
          error.textContent = caught.message;
          error.hidden = false;
          submit.disabled = false;
          submit.textContent = root.dataset.productionCheckout === "true" ? "Continue to secure checkout" : "Prepare test checkout";
        }
      });
      panel.focus();
    } catch (caught) {
      loading.hidden = true;
      loadError.textContent = `${caught.message} The link may have expired or the approved specification may have changed. Ask the curtain team for a new link.`;
      loadError.hidden = false;
      sessionStorage.removeItem(REVIEW_RESUME_KEY);
      panel.focus();
    }
    return true;
  }

  async function initConfigurator(root) {
    const resume = reviewedResumeCapability();
    if (await initReviewedAcceptance(root, resume)) return;
    const form = root.querySelector(".cuk-form");
    const reviewForm = root.querySelector("[data-cuk-review-form]");
    const reviewConfirmation = root.querySelector("[data-cuk-review-confirmation]");
    const checkoutForm = root.querySelector("[data-cuk-checkout-form]");
    const errorBox = root.querySelector("[data-cuk-error]");
    const result = root.querySelector("[data-cuk-result]");
    const windowSelect = form.elements.windowSlug;
    const fabricSelect = form.elements.fabricId;
    const params = new URLSearchParams(location.search);
    const mtm = window.CurtainsUKMtmConfigurator;
    if (!mtm) {
      errorBox.textContent = 'The curtain options could not load. Please refresh this page before checking a price.';
      errorBox.hidden = false;
      form.querySelector('button[type=submit]').disabled = true;
      return;
    }
    const priceGate = mtm.createRequestGate();
    let catalog;
    let lastEvaluation = null;
    const roomsEnabled = root.dataset.roomsEnabled === 'true';
    const roomIntent = roomsEnabled ? window.CurtainsUKRooms?.readIntent() : null;
    const savedHouseCurtains = (() => {
      try { return roomsEnabled ? window.CurtainsUKRooms?.totals(window.CurtainsUKRooms.read() || window.CurtainsUKRooms.ensure()).curtains || 0 : 0; }
      catch { return 0; }
    })();
    const orderEdit = roomsEnabled && params.get('order_item') ? window.CurtainsUKRooms?.orderItem(window.CurtainsUKRooms.ensure(), params.get('order_item')) : null;
    if (params.get('order_item') && (!orderEdit || orderEdit.type !== 'CURTAIN')) { errorBox.textContent='This order item could not be found. Return to Design My Home.'; errorBox.hidden=false; return; }
    const roomsFresh = !orderEdit && roomsEnabled && (params.get('rooms_new') === '1' || roomIntent !== null || savedHouseCurtains > 0);
    const checkoutLabel = () => roomsEnabled ? (orderEdit ? 'Update my order' : 'Add to my order') : root.dataset.productionCheckout === 'true' ? 'Continue to secure checkout' : 'Prepare test checkout';

    try {
      const requested = params.get("fabric") || (!roomsFresh ? readJson(PROJECT_KEY, {}).fabricId : "");
      if (requested && !FABRIC_MASTER_ID.test(requested)) throw new Error("We couldn't verify the fabric in this link. Please choose a fabric from Browse Fabrics.");
      catalog = await fetchJson(endpoint(root.dataset.engineBase, "catalog") + (requested ? `?fabric=${encodeURIComponent(requested)}` : ""));
      catalog.windows.filter((item) => AUTOMATED_MTM_WINDOWS.has(item.slug) && item.configuration?.routes?.length).forEach((item) => windowSelect.appendChild(option(item.name, item.slug)));
      if (windowSelect.options.length < 2) throw new Error('The curtain options could not load. Please refresh this page before checking a price.');
      fabricSelect.replaceChildren(option("Choose a fabric", ""));
      catalog.fabrics.filter((item) => item.configurable === true || item.selectableForReview === true).forEach((item) => fabricSelect.appendChild(option(`${item.brand || item.supplier} · ${item.design} · ${item.colour}`, item.id)));
    } catch (error) {
      errorBox.textContent = error.message;
      errorBox.hidden = false;
      form.querySelector("button[type=submit]").disabled = true;
      return;
    }

    const remembered = orderEdit ? {...orderEdit.curtain.configuration} : roomsFresh ? {} : readJson(PROJECT_KEY, {});
    restoreProject(form, remembered);
    const requestedFabric = params.get("fabric") || remembered.fabricId || "";
    if (roomsFresh) {
      beginNewCurtainDraft(root, form, requestedFabric);
      lastEvaluation = null;
    }
    if (roomsEnabled) {
      try { if (roomsFresh) localStorage.removeItem(EVALUATION_KEY); window.CurtainsUKRoomsFlow.prepare(root); }
      catch (error) { errorBox.textContent = error.message; errorBox.hidden = false; form.querySelector('button[type=submit]').disabled = true; return; }
    }
    const requestedWindow = roomsFresh ? "" : (orderEdit ? remembered.windowSlug : (params.get("window") || root.dataset.windowSlug || remembered.windowSlug || "standard-window"));
    windowSelect.value = AUTOMATED_MTM_WINDOWS.has(requestedWindow) ? requestedWindow : "";
    const configurableFabrics = catalog.fabrics.filter((item) => item.configurable === true || item.selectableForReview === true);
    fabricSelect.value = configurableFabrics.some((item) => item.id === requestedFabric)
      ? requestedFabric
      : (roomsFresh ? "" : configurableFabrics[0]?.id);
    if (requestedFabric && fabricSelect.value !== requestedFabric) {
      const resolution = catalog.requestedFabric;
      const unavailable = resolution?.id === requestedFabric && resolution.status === "NOT_FOUND";
      fabricSelect.appendChild(option(unavailable ? "Selected fabric is no longer available" : "Selected fabric needs confirmation", requestedFabric));
      fabricSelect.value = requestedFabric;
      form.querySelector("button[type=submit]").disabled = true;
      errorBox.textContent = unavailable
        ? "This fabric is no longer available to configure. Please choose another fabric."
        : "We couldn't confirm this fabric for made-to-measure curtains, so no price or checkout is available.";
      errorBox.hidden = false;
    }
    renderBaySections(root, remembered.baySectionCount || 3, remembered.baySectionWidthsCm || []);
    setJourneyFields(root, catalog.windows.find((item) => item.slug === windowSelect.value));
    // These options do not exist until the authoritative catalogue has loaded.
    // Restore their saved values through reconciliation, never directly into an empty select.
    mtm.sync(form, catalog.windows.find((item) => item.slug === windowSelect.value), {
      construction: remembered.construction || '', stackDirection: remembered.stackDirection || '',
    });
    rememberProject(projectSnapshot(form, root));
    syncConfiguratorUrl(form);
    if (roomsFresh) {
      const clean = new URL(location.href);
      clean.searchParams.delete('rooms_new');
      clean.searchParams.delete('window');
      history.replaceState(history.state, '', clean);
    }
    emit("configurator_started", { window_type: windowSelect.value, surface: "shopify_dawn" });

    function showSavedReview(response) {
      reviewForm.classList.add("cuk-hidden");
      checkoutForm.classList.add("cuk-hidden");
      result.hidden = false;
      reviewConfirmation.innerHTML = `<h2>Project received</h2><p>${escapeHtml(response.message || "Our curtain team will review the measurements and contact you with the next step.")}</p><p class="cuk-hint">Reference ${escapeHtml(response.reference || response.requestId)} · No payment has been taken.</p>`;
      const email = root.dataset.reviewEmail;
      if (email) {
        const link = document.createElement("a");
        link.className = "cuk-button";
        link.href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(`Curtain evidence ${response.reference || response.requestId}`)}`;
        link.textContent = `Email photos / drawings to ${email}`;
        reviewConfirmation.append(link);
      }

      reviewConfirmation.classList.remove("cuk-hidden");
      reviewConfirmation.focus();
    }
    let livePriceTimer;
    let livePriceSequence=0;
    let automaticSubmit = false;
    let observedState = JSON.stringify(mtm.read(form));
    const selectedWindow = () => catalog.windows.find(item => item.slug === windowSelect.value);
    const readyForPrice = () => !mtm.validate(selectedWindow(), mtm.read(form))
      && configurableFabrics.some(fabric => fabric.id === fabricSelect.value);
    function schedulePrice() {
      clearTimeout(livePriceTimer);
      const sequence = livePriceSequence;
      livePriceTimer = setTimeout(() => {
        if (sequence !== livePriceSequence || !readyForPrice() || !priceGate.canSubmit(mtm.read(form), true)) return;
        automaticSubmit = true;
        form.requestSubmit();
        automaticSubmit = false;
      }, 650);
    }
    const invalidatePrice = () => {
      const state = mtm.read(form);
      const fingerprint = JSON.stringify(state);
      if (fingerprint === observedState) return;
      observedState = fingerprint;
      priceGate.changed(state);
      rememberProject(projectSnapshot(form, root));
      lastEvaluation = null;
      result.hidden = true;
      checkoutForm.classList.add('cuk-hidden');
      reviewForm.classList.add('cuk-hidden');
      reviewConfirmation.classList.add('cuk-hidden');
      const checkoutButton = checkoutForm.querySelector('button[type=submit]');
      if (checkoutButton) { checkoutButton.disabled = false; checkoutButton.textContent = checkoutLabel(); }
      checkoutForm.querySelector('[data-cuk-checkout-confirmation]')?.classList.add('cuk-hidden');
      livePriceSequence++;
      schedulePrice();
    };
    form.addEventListener('input', invalidatePrice);
    form.addEventListener('change', invalidatePrice);
    for (const name of ['heading', 'construction']) form.elements[name].addEventListener('change', () => {
      mtm.sync(form, selectedWindow());
      rememberProject(projectSnapshot(form, root));
    });
    form.addEventListener('reset', () => {
      livePriceSequence++;
      clearTimeout(livePriceTimer);
      lastEvaluation = null;
      result.hidden = true;
      checkoutForm.classList.add('cuk-hidden');
      // The browser resets field values after this event. Do not price that reset.
      setTimeout(() => {
        mtm.sync(form, selectedWindow());
        const state = mtm.read(form);
        observedState = JSON.stringify(state);
        priceGate.changed(state);
      }, 0);
    });
    const savedReceipt = readJson(RECEIPT_KEY, null);
    if (savedReceipt && JSON.stringify(savedReceipt.project) === JSON.stringify(projectSnapshot(form, root))) {
      showSavedReview(savedReceipt.receipt);
    }

    windowSelect.addEventListener("change", () => {
      const selected = catalog.windows.find((item) => item.slug === windowSelect.value);
      setJourneyFields(root, selected);
      result.hidden = true;
      reviewForm.classList.add("cuk-hidden");
      checkoutForm.classList.add("cuk-hidden");
      reviewConfirmation.classList.add("cuk-hidden");
      rememberProject(projectSnapshot(form, root));
      syncConfiguratorUrl(form);
      emit("window_type_selected", { window_type: windowSelect.value });
    });
    form.elements.measurementBasis.addEventListener("change", () => {
      const selected = catalog.windows.find((item) => item.slug === windowSelect.value);
      setJourneyFields(root, selected);
      result.hidden = true;
      checkoutForm.classList.add("cuk-hidden");
      rememberProject(projectSnapshot(form, root));
    });
    fabricSelect.addEventListener("change", () => {
      form.querySelector("button[type=submit]").disabled = !configurableFabrics.some((fabric) => fabric.id === fabricSelect.value);
      rememberProject(projectSnapshot(form, root));
      syncConfiguratorUrl(form);
      emit("fabric_selected", { fabric_id: fabricSelect.value, window_type: windowSelect.value });
    });
    form.elements.baySectionCount?.addEventListener("change", () => {
      const existing = baySectionWidths(root);
      renderBaySections(root, form.elements.baySectionCount.value, existing);
      rememberProject(projectSnapshot(form, root));
    });
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      clearTimeout(livePriceTimer);
      errorBox.hidden = true;
      result.hidden = true;
      const submittedState = mtm.read(form);
      const problem = mtm.validate(selectedWindow(), submittedState);
      if (problem) {
        if (!automaticSubmit) {
          errorBox.textContent = problem.message;
          errorBox.hidden = false;
          root.dispatchEvent(new CustomEvent('cuk:configuration-field-needed', { detail: { field: problem.field } }));
        }
        return;
      }
      if (!readyForPrice() || !priceGate.canSubmit(submittedState, automaticSubmit)) {
        const rejection = priceGate.rejection(submittedState);
        if (rejection) { errorBox.textContent = rejection; errorBox.hidden = false; }
        return;
      }
      if (!form.reportValidity()) {
        emit("validation_failure", { window_type: windowSelect.value, source: "browser" });
        return;
      }
      const selected = catalog.windows.find((item) => item.slug === windowSelect.value);
      emit("measurement_completion", { window_type: windowSelect.value, journey: selected.journey });
      const photoNames = [];
      let path = "price";
      let body;

      if (!AUTOMATED_MTM_WINDOWS.has(selected.slug)) {
        throw new Error("This opening needs a curtain-team review and is not available for automated checkout.");
      }
      if (selected.journey === "SPECIALIST") {
        const isAwkward = selected.slug === "awkward-unusual-window";
        path = "specialist-review";
        body = {
          windowSlug: windowSelect.value,
          measurements: isAwkward
            ? {
                coverage_width: Number(form.elements.roughWidthCm.value),
                finished_drop: Number(form.elements.roughDropCm.value),
              }
            : {
                coverage_width: Number(form.elements.baseWidth.value),
                peak_height: Number(form.elements.peakHeight.value),
                left_vertical: Number(form.elements.leftVertical.value),
                right_vertical: Number(form.elements.rightVertical.value),
                left_slope: Number(form.elements.leftSlope.value),
                right_slope: Number(form.elements.rightSlope.value),
              },
          fabricId: fabricSelect.value,
          heading: form.elements.heading.value,
          lining: form.elements.lining.value,
          construction: form.elements.construction.value,
          fixingPosition: isAwkward ? form.elements.awkwardFixingPosition.value : form.elements.fixingPosition.value,
          stackDirection: form.elements.stackDirection.value,
          photoNames,
        };
      } else {
        const isBay = selected.slug === "bay-window";
        const measurementBasis = isBay ? "TRACK_WIDTH" : form.elements.measurementBasis.value;
        if (!["TRACK_WIDTH", "POLE_USABLE_WIDTH"].includes(measurementBasis)) {
          errorBox.textContent = "Choose your fitted track or pole before checking the price.";
          errorBox.hidden = false;
          form.elements.measurementBasis.focus();
          return;
        }
        body = {
          windowSlug: windowSelect.value,
          measurementBasis,
          hardware: measurementBasis === "TRACK_WIDTH" ? "TRACK" : "POLE",
          widthCm: Number(form.elements.widthCm.value),
          dropCm: Number(form.elements.dropCm.value),
          fabricId: fabricSelect.value,
          heading: form.elements.heading.value,
          lining: form.elements.lining.value,
          construction: form.elements.construction.value,
          stackDirection: form.elements.stackDirection.value,
          photoNames,
        };
      }

      const submittedSequence = livePriceSequence;
      priceGate.start(submittedState);
      const submit = form.querySelector("button[type=submit]");
      submit.disabled = true;
      submit.textContent = "Checking…";
      try {
        emit("configurator_step_completed", { step: "specification", window_type: windowSelect.value });
        const savedEvaluation = readJson(EVALUATION_KEY, null);
        const matchingSaved = savedEvaluation && JSON.stringify(savedEvaluation.configuration) === JSON.stringify(body);
        const tokenExpiry = Number(savedEvaluation?.calculation?.reviewSubmissionToken?.split(".")[1] || 0);
        if (matchingSaved && savedEvaluation.checkoutAttempted && tokenExpiry <= Date.now() / 1000 + 30) {
          throw new Error(`Your previous test order needs confirmation. Contact the curtain team quoting configuration ${savedEvaluation.calculation.configurationId}. Do not start a replacement order.`);
        }
        const response = matchingSaved && tokenExpiry > Date.now() / 1000 + 30
          ? savedEvaluation.calculation
          : await fetchJson(endpoint(root.dataset.engineBase, path), { method: "POST", body: JSON.stringify(body), headers: { 'Content-Type': 'application/json', 'X-CurtainsUK-Storefront-Release': mtm.release } });
        if (submittedSequence !== livePriceSequence) return;
        // Refresh retired presentation copy without changing a resumed price or identity.
        const deliveryLabel = response.delivery === "UK delivery shown separately; postcode and packed parcel confirmation required"
          ? "UK delivery shown separately after postcode confirmation. Specialist delivery confirmed after review"
          : response.delivery || "Delivery shown separately";
        localStorage.setItem(EVALUATION_KEY, JSON.stringify({configuration: body, calculation: response, checkoutAttempted: matchingSaved && savedEvaluation.checkoutAttempted === true}));
        const isManualQuote = response.outcome === "MANUAL_QUOTE";
        const isPriceWithReview = response.outcome === "PRICE_WITH_REVIEW";
        const needsReview = isManualQuote || isPriceWithReview;
        const routeStatus = root.querySelector("[data-cuk-route-status]");
        routeStatus.textContent = isManualQuote ? "Manual quote" : isPriceWithReview ? "Price with review" : "Instant price";
        routeStatus.classList.toggle("cuk-status--review", needsReview);
        const evidence = root.querySelector("[data-cuk-review-evidence]");
        if (needsReview && evidence) {
          evidence.classList.remove("cuk-hidden");
          evidence.querySelectorAll("input, select, textarea").forEach((field) => { field.disabled = false; });
          if (selected.journey !== "SPECIALIST") {
            evidence.querySelectorAll("[data-cuk-specialist-evidence]").forEach((item) => item.classList.add("cuk-hidden"));
            evidence.querySelectorAll("[data-cuk-specialist-evidence] input, [data-cuk-specialist-evidence] select, [data-cuk-specialist-evidence] textarea").forEach((field) => { field.disabled = true; });
          }
        }
        const selectedFabric = response.selectedFabric || catalog.fabrics.find((fabric) => fabric.id === fabricSelect.value);
        const widthSummary = selected.slug === "bay-window"
          ? `${body.widthCm} cm full fitted bay-track route × ${body.dropCm} cm drop`
          : selected.slug === "corner-window"
            ? `${body.widthCm} cm total across two sections · ${body.cornerAngleDegrees}° corner`
            : selected.slug === "curved-bow-window"
              ? `${body.widthCm} cm track arc × ${body.dropCm} cm drop`
          : selected.journey === "SPECIALIST"
            ? selected.slug === "awkward-unusual-window"
              ? `${body.measurements.coverage_width} cm rough width × ${body.measurements.finished_drop} cm rough height`
              : `${body.measurements.coverage_width} cm base × ${body.measurements.peak_height} cm peak`
            : `${body.widthCm} cm width × ${body.dropCm} cm drop`;
        const outcomeBadge = result.querySelector("[data-cuk-result-outcome]");
        outcomeBadge.textContent = response.outcome.replaceAll("_", " ");
        outcomeBadge.classList.toggle("cuk-status--review", needsReview);
        const price = result.querySelector("[data-cuk-result-price]");
        price.classList.toggle("cuk-result__price--message", isManualQuote);
        price.textContent = isManualQuote
          ? response.commercialState === "PRICE_CONFIRMATION_REQUIRED" ? "Price confirmation required" : "Price confirmed after technical review"
          : Number.isFinite(response.totalAmountMinor)
            ? `${isPriceWithReview ? "Provisional price " : ""}${money(response.totalAmountMinor, response.currency)}`
            : "Technical review required";
        result.querySelector("[data-cuk-result-message]").textContent = [
          response.message,

          response.availability || "Availability to be confirmed",
          deliveryLabel,
        ].filter(Boolean).join(" · ");
        const priceLabel = isManualQuote
          ? "Confirmed after technical review"
          : Number.isFinite(response.totalAmountMinor)
            ? `${money(response.totalAmountMinor, response.currency)}`
            : "Not available";
        result.querySelector("[data-cuk-summary-window]").textContent = selected.name;
        result.querySelector("[data-cuk-summary-dimensions]").textContent = widthSummary;
        result.querySelector("[data-cuk-summary-fabric]").textContent = selectedFabric
          ? `${selectedFabric.design} · ${selectedFabric.colour}`
          : "Fabric selection unavailable";
        result.querySelector("[data-cuk-summary-heading]").textContent = form.elements.heading.selectedOptions[0]?.textContent || form.elements.heading.value;
        result.querySelector("[data-cuk-summary-lining]").textContent = form.elements.lining.selectedOptions[0]?.textContent || form.elements.lining.value;
        result.querySelector("[data-cuk-summary-construction]").textContent = form.elements.construction.selectedOptions[0]?.textContent || form.elements.construction.value;
        result.querySelector("[data-cuk-summary-availability]").textContent = response.availability || "Availability to be confirmed";
        result.querySelector("[data-cuk-summary-price]").textContent = priceLabel;
        result.querySelector("[data-cuk-summary-delivery]").textContent = deliveryLabel;
        result.querySelector("[data-cuk-summary-review]").textContent = needsReview ? "Required before checkout" : "Not required";
        result.querySelector("[data-cuk-result-spec]").textContent = [
          selected.name,
          widthSummary,
          selectedFabric ? `${selectedFabric.design} · ${selectedFabric.colour}` : null,
          form.elements.heading.selectedOptions[0]?.textContent,
          form.elements.lining.selectedOptions[0]?.textContent,
          form.elements.construction.selectedOptions[0]?.textContent,
          response.fabricWidths ? `${response.fabricWidths} fabric widths` : null,
        ].filter(Boolean).join(" · ");
        lastEvaluation = { configuration: body, calculation: response };
        reviewForm.classList.toggle("cuk-hidden", !needsReview);
        checkoutForm.classList.toggle("cuk-hidden", needsReview || isManualQuote || response.outcome !== "INSTANT_PRICE");
        // Preserve the delivery destination while requiring acceptance of every new price.
        checkoutForm.elements.customerAccepted.checked = false;
        const previousCheckoutError = checkoutForm.querySelector("[data-cuk-checkout-error]");
        previousCheckoutError.hidden = true;
        previousCheckoutError.textContent = "";
        checkoutForm.querySelector("[data-cuk-checkout-confirmation]")?.classList.add("cuk-hidden");
        const checkoutButton = checkoutForm.querySelector("button[type=submit]");
        if (checkoutButton) { checkoutButton.disabled = false; checkoutButton.textContent = checkoutLabel(); }
        const notice = result.querySelector("[data-cuk-result-notice]");
        if (notice) notice.textContent = needsReview
          ? "Checkout is unavailable. This project must be reviewed before payment or manufacture."
          : root.dataset.productionCheckout === "true" ? "Delivery and current availability are confirmed before checkout." : "Staging price only. Shopify test checkout is available only when every launch gate passes; real payment remains disabled.";
        reviewConfirmation.classList.add("cuk-hidden");
        result.hidden = false;
        const receipt = readJson(RECEIPT_KEY, null);
        if (receipt && JSON.stringify(receipt.project) === JSON.stringify(projectSnapshot(form, root))) showSavedReview(receipt.receipt);
        rememberProject({ ...projectSnapshot(form, root), lastOutcome: response.outcome });
        if (Number.isFinite(response.totalAmountMinor) && !isManualQuote) {
          emit("price_displayed", { window_type: windowSelect.value, outcome: response.outcome, amount_minor: response.totalAmountMinor, currency: response.currency, calculation_version: response.calculationVersion || null });
        }
      } catch (error) {
        if (error.status === 400) priceGate.reject(submittedState, error.diagnosticCode, error.message);
        if (submittedSequence !== livePriceSequence) return;
        errorBox.textContent = error.message;
        errorBox.hidden = false;
        emit("validation_failure", { window_type: windowSelect.value, source: "server" });
      } finally {
        priceGate.finish();
        submit.disabled = false;
        submit.textContent = selected.journey === "SPECIALIST" ? "Prepare my review" : "Check my price or review route";
        if (submittedSequence !== livePriceSequence && readyForPrice()) schedulePrice();
      }
    });

    reviewForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      const reviewError = reviewForm.querySelector("[data-cuk-review-error]");
      reviewError.hidden = true;
      if (!reviewForm.reportValidity() || !lastEvaluation) return;
      const payload = new FormData();
      payload.set("configuration", JSON.stringify(lastEvaluation.configuration));
      payload.set("calculation", JSON.stringify(lastEvaluation.calculation));
      payload.set("contactName", reviewForm.elements.customerName.value);
      payload.set("contactEmail", reviewForm.elements.customerEmail.value);
      payload.set("contactPhone", reviewForm.elements.customerPhone.value);
      payload.set("notes", reviewForm.elements.customerNotes.value);

      const submit = reviewForm.querySelector("button[type=submit]");
      submit.disabled = true;
      submit.textContent = "Submitting…";
      try {
        const response = await fetchJson(endpoint(root.dataset.engineBase, root.dataset.reviewPath || "review-request"), { method: "POST", body: payload });
        reviewForm.classList.add("cuk-hidden");
        localStorage.setItem(RECEIPT_KEY, JSON.stringify({project: projectSnapshot(form, root), receipt: response}));
        showSavedReview(response);
        emit("review_submitted", { window_type: form.elements.windowSlug.value, outcome: lastEvaluation.calculation.outcome, configuration_id: response.configurationId || null });
      } catch (error) {
        reviewError.textContent = error.message;
        reviewError.hidden = false;
      } finally {
        submit.disabled = false;
        submit.textContent = "Submit project for review";
      }
    });

    checkoutForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (root.dataset.purchaseControlsEnabled !== "true") return;
      const checkoutError = checkoutForm.querySelector("[data-cuk-checkout-error]");
      checkoutError.hidden = true;
      if (!checkoutForm.reportValidity() || !lastEvaluation || lastEvaluation.calculation.outcome !== "INSTANT_PRICE") return;
      if (roomsEnabled) {
        const button = checkoutForm.querySelector('button[type=submit]');
        button.disabled = true; button.textContent = 'Updating Design My Home…';
        try {
          await window.CurtainsUKRoomsFlow.add(lastEvaluation, root.dataset.engineBase, {
            roomId: checkoutForm.elements.roomId.value || null,
            roomName: checkoutForm.elements.roomName.value,
            windowName: checkoutForm.elements.windowName.value,
          });
        } catch (error) {
          checkoutError.textContent = error.message; checkoutError.hidden = false;
          button.disabled = false; button.textContent = checkoutLabel();
        }
        return;
      }
      const previousAttempt = readJson(EVALUATION_KEY, {}).checkoutAttempted === true;
      const submit = checkoutForm.querySelector("button[type=submit]");
      submit.disabled = true;
      submit.textContent = "Validating and freezing…";
      emit("quote_accepted", {
        window_type: lastEvaluation.configuration.windowSlug,
        outcome: lastEvaluation.calculation.outcome,
        configuration_id: lastEvaluation.calculation.configurationId,
      });
      try {
        localStorage.setItem(EVALUATION_KEY, JSON.stringify({...lastEvaluation, checkoutAttempted: true}));
        const hciContext = readJson('curtainsuk_hci_context_v1', null);
        const handoff = await fetchJson(endpoint(root.dataset.engineBase, "checkout-handoff"), {
          method: "POST",
          body: JSON.stringify({
            configuration: lastEvaluation.configuration,
            configurationId: lastEvaluation.calculation.configurationId,
            priceConfirmationToken: lastEvaluation.calculation.reviewSubmissionToken,
            ...(hciContext?.fabricMasterId === lastEvaluation.configuration.fabricId && hciContext.commerceToken ? {hciCommerceToken:hciContext.commerceToken} : {}),
            customerAccepted: checkoutForm.elements.customerAccepted.checked,
            shippingRegion: checkoutForm.elements.shippingRegion.value,
            shippingPostcode: checkoutForm.elements.shippingPostcode.value,
            openaiAttribution: openAiAttribution(),
          }),
        });
        if (!handoff.prepared) {
          localStorage.setItem(EVALUATION_KEY, JSON.stringify({...lastEvaluation, checkoutAttempted: previousAttempt}));
          const blockers = Array.isArray(handoff.blockers) ? handoff.blockers.map((item) => String(item).replaceAll("_", " ").toLowerCase()).join(", ") : "a launch gate";
          throw new Error(`${handoff.message || "This configuration is not ready for checkout"} (${blockers}).`);
        }
        const confirmation = checkoutForm.querySelector("[data-cuk-checkout-confirmation]");
        renderHandoffConfirmation(root, confirmation, handoff);
        submit.textContent = handoff.paymentEnabled ? "Secure checkout prepared" : handoff.checkoutUrl ? "Test checkout prepared" : "Total validated";
        result.querySelector("[data-cuk-summary-review]").textContent = handoff.paymentEnabled ? "Secure checkout ready" : handoff.checkoutUrl ? "Shopify test checkout ready" : "Validated for staging";
        emit("checkout_handoff_reached", {
          window_type: lastEvaluation.configuration.windowSlug,
          outcome: lastEvaluation.calculation.outcome,
          configuration_id: lastEvaluation.calculation.configurationId,
          handoff_id: handoff.handoffId,
          amount_minor: handoff.goodsPriceGrossAmountMinor + handoff.shippingGrossAmountMinor,
          currency: handoff.currency || "GBP",
          order_kind: "curtain",
          content_id: handoff.snapshotId,
          content_type: "curtain_order",
        });
      } catch (error) {
        if (!previousAttempt && error.message.includes("previous price confirmation")) localStorage.removeItem(EVALUATION_KEY);
        checkoutError.textContent = error.message;
        checkoutError.hidden = false;
        submit.disabled = false;
        submit.textContent = root.dataset.productionCheckout === "true" ? "Continue to secure checkout" : "Prepare test checkout";
      }
    });
  }

  const fabricBrowserInstances = new WeakMap();
  function mountFabricBrowser(root) {
    if (fabricBrowserInstances.has(root)) return fabricBrowserInstances.get(root);
    const lifecycle = { listeners: new AbortController(), disposed: false, reload: null, cancel: null };
    const api = {
      ready: null,
      reload: () => lifecycle.disposed ? Promise.resolve() : api.ready.then(() => lifecycle.reload?.()),
      dispose() { lifecycle.disposed = true; lifecycle.cancel?.(); lifecycle.listeners.abort(); fabricBrowserInstances.delete(root); },
    };
    fabricBrowserInstances.set(root, api);
    api.ready = initFabricBrowser(root, lifecycle);
    return api;
  }
  window.CurtainsUKFabricBrowser = { mount: mountFabricBrowser };
  document.querySelectorAll("[data-cuk-configurator]").forEach(initConfigurator);
  document.querySelectorAll("[data-cuk-fabric-browser]").forEach(root => { if (!root.hasAttribute('data-cuk-browser-deferred')) mountFabricBrowser(root); });
  renderBaskets();
})();
